import { supabase } from '../supabase';

export const dashboardService = {
  /**
   * Get comprehensive dashboard statistics
   */
  getDashboardStats: async () => {
    try {
      // Get invoice stats
      const { data: invoices } = await supabase
        .from('invoices')
        .select('status, total');

      // Get expense stats
      const { data: expenses } = await supabase
        .from('expenses')
        .select('amount');

      // Get payment stats
      const { data: payments } = await supabase
        .from('payments')
        .select('amount');

      if (!invoices || !expenses || !payments) {
        return { data: null, error: 'Failed to fetch dashboard stats' };
      }

      const totalRevenue = payments.reduce((sum, payment) => sum + Number(payment.amount), 0);
      const totalOutstanding = invoices
        .filter(inv => inv.status !== 'paid')
        .reduce((sum, inv) => sum + Number(inv.total), 0);
      const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
      
      const stats = {
        totalRevenue,
        totalOutstanding,
        totalExpenses,
        invoiceCount: invoices.length,
        paidInvoices: invoices.filter(inv => inv.status === 'paid').length,
        overdueInvoices: invoices.filter(inv => inv.status === 'overdue').length,
        draftInvoices: invoices.filter(inv => inv.status === 'draft').length,
        sentInvoices: invoices.filter(inv => inv.status === 'sent').length,
        netProfit: totalRevenue - totalExpenses,
        averageInvoiceValue: invoices.length > 0 ? totalRevenue / invoices.length : 0,
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { data: null, error };
    }
  },

  /**
   * Get revenue data for charts
   */
  getRevenueData: async (months: number = 6) => {
    const { data, error } = await supabase
      .rpc('get_monthly_revenue', { months_back: months });
    return { data, error };
  },

  /**
   * Get recent activity
   */
  getRecentActivity: async (limit: number = 10) => {
    const { data: recentInvoices } = await supabase
      .from('invoices')
      .select(`
        id,
        invoice_number,
        total,
        status,
        created_at,
        client:clients(name, company)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    const { data: recentPayments } = await supabase
      .from('payments')
      .select(`
        id,
        amount,
        payment_date,
        created_at,
        invoice:invoices(
          invoice_number,
          client:clients(name, company)
        )
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    return {
      invoices: recentInvoices || [],
      payments: recentPayments || [],
    };
  },

  /**
   * Get client statistics
   */
  getClientStats: async () => {
    const { data: clients } = await supabase
      .from('clients')
      .select('id');

    const { data: activeClients } = await supabase
      .from('invoices')
      .select('client_id')
      .gte('created_at', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString());

    const uniqueActiveClients = new Set(activeClients?.map(inv => inv.client_id) || []);

    return {
      totalClients: clients?.length || 0,
      activeClients: uniqueActiveClients.size,
    };
  }
};

// Legacy export for backward compatibility
export { dashboardService as dbHelpers };